import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  updateMyStatusThunk,
  getAdminStatusThunk,
} from "../../../../store/thunks/adminThunks";
import Button from "../../../../components/common/Button";
import Badge from "../../../../components/common/Badge";
import Input from "../../../../components/common/Input";
import {
  StatusOnlineIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
} from "@heroicons/react/outline";

const StatusManager = ({ currentStatus, admin }) => {
  const dispatch = useDispatch();
  const { loading, adminStatuses } = useSelector((state) => state.admin);

  // Local state
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [customMessage, setCustomMessage] = useState("");
  const [showMessageInput, setShowMessageInput] = useState(false);

  // Status definitions
  const statusOptions = [
    {
      value: "AVAILABLE",
      label: "Available",
      color: "success",
      bgColor: "bg-green-50",
      textColor: "text-green-700",
      borderColor: "border-green-200",
      icon: "✅",
      description: "Ready to take appointments and assist customers",
    },
    {
      value: "BUSY",
      label: "Busy",
      color: "warning",
      bgColor: "bg-yellow-50",
      textColor: "text-yellow-700",
      borderColor: "border-yellow-200",
      icon: "⚡",
      description: "Currently engaged but can handle urgent requests",
    },
    {
      value: "IN_APPOINTMENT",
      label: "In Appointment",
      color: "info",
      bgColor: "bg-blue-50",
      textColor: "text-blue-700",
      borderColor: "border-blue-200",
      icon: "👥",
      description: "Currently with a customer",
    },
    {
      value: "ON_BREAK",
      label: "On Break",
      color: "warning",
      bgColor: "bg-orange-50",
      textColor: "text-orange-700",
      borderColor: "border-orange-200",
      icon: "☕",
      description: "Taking a break, will return shortly",
    },
    {
      value: "OFF_DUTY",
      label: "Off Duty",
      color: "default",
      bgColor: "bg-gray-50",
      textColor: "text-gray-700",
      borderColor: "border-gray-200",
      icon: "🏠",
      description: "Not working currently",
    },
    {
      value: "AWAY",
      label: "Away",
      color: "danger",
      bgColor: "bg-red-50",
      textColor: "text-red-700",
      borderColor: "border-red-200",
      icon: "🚫",
      description: "Temporarily away from desk",
    },
  ];

  // Quick status presets with messages
  const quickPresets = [
    { status: "AVAILABLE", message: "" },
    { status: "ON_BREAK", message: "Lunch break - back at 2 PM" },
    { status: "BUSY", message: "In a meeting" },
    { status: "AWAY", message: "Stepped out for a moment" },
  ];

  // Initialize selected status
  useEffect(() => {
    if (currentStatus?.status) {
      const status = statusOptions.find(
        (s) => s.value === currentStatus.status
      );
      setSelectedStatus(status);
      setCustomMessage(currentStatus.customMessage || "");
    }
  }, [currentStatus]);

  // Get status config
  const getStatusConfig = (statusValue) => {
    return (
      statusOptions.find((s) => s.value === statusValue) || statusOptions[0]
    );
  };

  // Handle status change
  const handleStatusChange = async (status) => {
    setSelectedStatus(status);
    setShowMessageInput(false);
    setCustomMessage("");
  };

  // Handle save
  const handleSave = async () => {
    if (!selectedStatus) return;

    try {
      await dispatch(
        updateMyStatusThunk({
          status: selectedStatus.value,
          message: customMessage || undefined,
        })
      ).unwrap();

      setShowMessageInput(false);
    } catch (error) {
      console.error("Failed to update status:", error);
    }
  };

  // Handle quick preset
  const handleQuickPreset = async (preset) => {
    const status = statusOptions.find((s) => s.value === preset.status);
    setSelectedStatus(status);
    setCustomMessage(preset.message);

    try {
      await dispatch(
        updateMyStatusThunk({
          status: preset.status,
          message: preset.message || undefined,
        })
      ).unwrap();
    } catch (error) {
      console.error("Failed to update status:", error);
    }
  };

  // Format last updated time
  const formatLastUpdated = (date) => {
    if (!date) return "Never";
    const now = new Date();
    const updated = new Date(date);
    const diffMs = now - updated;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60)
      return `${diffMins} minute${diffMins > 1 ? "s" : ""} ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24)
      return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
    return updated.toLocaleDateString();
  };

  const currentStatusConfig = getStatusConfig(currentStatus?.status);
  const hasChanges =
    selectedStatus?.value !== currentStatus?.status ||
    customMessage !== (currentStatus?.customMessage || "");

  return (
    <div className="space-y-6">
      {/* Current Status Display */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="text-3xl">{currentStatusConfig.icon}</div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Current Status
              </h3>
              <p className="text-sm text-gray-600">
                Last updated {formatLastUpdated(currentStatus?.lastUpdated)}
              </p>
            </div>
          </div>
          <Badge
            variant={currentStatusConfig.color}
            className="text-sm px-4 py-2"
          >
            {currentStatusConfig.label}
          </Badge>
        </div>

        {currentStatus?.customMessage && (
          <div className="bg-white rounded-lg p-3 border">
            <p className="text-sm text-gray-700">
              💬 {currentStatus.customMessage}
            </p>
          </div>
        )}

        {currentStatus?.autoUpdated && (
          <div className="mt-3 flex items-center gap-2 text-xs text-gray-600">
            <InformationCircleIcon className="w-4 h-4" />
            <span>Status was automatically updated based on your schedule</span>
          </div>
        )}
      </div>

      {/* Quick Status Presets */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <ClockIcon className="w-4 h-4 text-primary" />
          Quick Status Updates
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {quickPresets.map((preset, index) => {
            const config = getStatusConfig(preset.status);
            const isActive = currentStatus?.status === preset.status;

            return (
              <button
                key={index}
                onClick={() => handleQuickPreset(preset)}
                disabled={loading}
                className={`p-4 rounded-lg border-2 transition-all ${
                  isActive
                    ? `${config.borderColor} ${config.bgColor} ring-2 ring-offset-2 ring-${config.color}-500`
                    : "border-gray-200 hover:border-gray-300 bg-white"
                } ${
                  loading ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
                }`}
              >
                <div className="text-2xl mb-2">{config.icon}</div>
                <p className="font-medium text-sm">{config.label}</p>
                {preset.message && (
                  <p className="text-xs text-gray-600 mt-1 truncate">
                    {preset.message}
                  </p>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Detailed Status Selection */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <StatusOnlineIcon className="w-4 h-4 text-primary" />
          Change Status
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {statusOptions.map((status) => {
            const isSelected = selectedStatus?.value === status.value;
            const isCurrent = currentStatus?.status === status.value;

            return (
              <button
                key={status.value}
                onClick={() => handleStatusChange(status)}
                disabled={loading}
                className={`p-4 rounded-lg border-2 text-left transition-all ${
                  isSelected
                    ? `${status.borderColor} ${status.bgColor} ring-2 ring-offset-1 ring-primary`
                    : isCurrent
                    ? `${status.borderColor} ${status.bgColor}`
                    : "border-gray-200 hover:border-gray-300 bg-white"
                } ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{status.icon}</span>
                    <div>
                      <p className="font-medium text-gray-900 flex items-center gap-2">
                        {status.label}
                        {isCurrent && (
                          <CheckCircleIcon className="w-4 h-4 text-green-600" />
                        )}
                      </p>
                      <p className="text-xs text-gray-600 mt-0.5">
                        {status.description}
                      </p>
                    </div>
                  </div>
                  {isSelected && (
                    <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Custom Message */}
      {selectedStatus && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              💬 Custom Message (Optional)
            </h3>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowMessageInput(!showMessageInput)}
            >
              {showMessageInput ? "Hide" : "Add Message"}
            </Button>
          </div>

          {showMessageInput && (
            <div className="bg-gray-50 rounded-lg p-4 border">
              <Input
                type="text"
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                placeholder="e.g., Back at 3 PM, In a meeting, etc."
                maxLength={100}
                helperText={`${customMessage.length}/100 characters`}
              />
              <div className="mt-3 flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setCustomMessage("");
                    setShowMessageInput(false);
                  }}
                >
                  Clear
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Save Button */}
      {hasChanges && (
        <div className="sticky bottom-0 bg-white border-t pt-4 mt-6 flex justify-between items-center">
          <p className="text-sm text-orange-600 font-medium flex items-center gap-2">
            <ExclamationCircleIcon className="w-4 h-4" />
            You have unsaved changes
          </p>
          <div className="flex gap-3">
            <Button
              variant="secondary"
              onClick={() => {
                const current = statusOptions.find(
                  (s) => s.value === currentStatus?.status
                );
                setSelectedStatus(current);
                setCustomMessage(currentStatus?.customMessage || "");
                setShowMessageInput(false);
              }}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={loading || !selectedStatus}
            >
              {loading ? "Updating..." : "Update Status"}
            </Button>
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
        <div className="flex gap-3">
          <InformationCircleIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">About Status Updates</p>
            <ul className="space-y-1 list-disc list-inside">
              <li>Your status helps customers know your availability</li>
              <li>Status may auto-update based on your working hours</li>
              <li>
                Custom messages are visible to customers booking appointments
              </li>
              <li>Use quick presets for common status changes</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatusManager;
